import CustomDropDownMenu from "@components/FormFields/CustomDropDownMenu";
import { SignageContentPlayer } from "@components/Players";
import { UpdateScreenPlaying } from "@components/screens/SelectScreenContentDialog";
import { Button } from "@components/ui/button";
import useDialog, { DialogProvider } from "@hooks/useDialog";
import { useLayouts } from "@hooks/useLayouts";
import { ScreenPlayingOptionType } from "@hooks/useScreens/types";
import { Cross2Icon } from "@radix-ui/react-icons";
import { getAspectRatio } from "@utils/index";
import { useEffect, useMemo, useState } from "react";
import { FiMonitor } from "react-icons/fi";
import { MdDone, MdFormatPaint } from "react-icons/md";
import { PiTelevisionSimpleBold } from "react-icons/pi";
import { Screen } from "types/index";
import {
  CircularProgressIndicator,
  LoadingContainer,
} from "../CircularProgressIndicator";

type Props = {
  opt: ScreenPlayingOptionType;
  onChange: (val: ScreenPlayingOptionType, openInNew: boolean) => void;
  updateScreenPlaying: UpdateScreenPlaying;
  screen?: Screen;
  screens?: Screen[];
  duration?: number;
  orgId: string;
  userId: string;
  imageFit?: string;
  closeInitialDialog?: () => void;
};
export function useContentPreviewDialog() {
  const { openDialog, closeDialog } = useDialog();
  return {
    openContentPreviewDialog: (props: Props) => {
      openDialog({
        showActions: false,
        maxWidth: "80vw",
        height: "90vh",
        dialogType: "loading",
        hideCloseButton: true,
        customContent: (
          <ContentPreviewDialog {...props} closeInitialDialog={closeDialog} />
        ),
      });
    },
  };
}
export function ContentPreviewDialog(props: Props) {
  return (
    <DialogProvider>
      <ContentPreviewDialogPresenter {...props} />
    </DialogProvider>
  );
}

function ContentPreviewDialogPresenter({
  opt,
  updateScreenPlaying,
  screen: initialScreen,
  onChange,
  duration,
  orgId,
  imageFit,
  userId,
  screens = [],
  closeInitialDialog,
}: Props) {
  const { cloneDesign: _cloneDesign } = useLayouts()
  const [screen, setScreen] = useState(initialScreen);
  const [previewLoading, setPreviewLoading] = useState(false);
  const {
    showErrorSnackbar,
    showSuccessSnackbar,
    closeDialog,
    openLoadingOverlay,
  } = useDialog();

  const [pendingContentChange, setPendingContentChange] = useState<{
    screenId: string;
    opt: ScreenPlayingOptionType;
  }>();

  async function onPreview({
    option,
    screenId,
    openInNew,
    changeState = true,
  }: {
    option?: ScreenPlayingOptionType;
    screenId?: string;
    openInNew?: boolean;
    changeState?: boolean;
  }) {
    const _screenId = screenId ?? screen?.id;

    if (!_screenId) return;
    if (changeState) setPreviewLoading(true);
    const _opt = option ?? opt;

    const _data: {
      screenId: string;
      opt: ScreenPlayingOptionType;
      imageFit?: string;
      orgId: string;
      content_synced?: boolean;
    } = { screenId: _screenId, opt: _opt, orgId };

    if (opt.type === "asset") {
      _data["imageFit"] = imageFit;
    }

    updateScreenPlaying(_data, {
      onError() {
        if (changeState) {
          setPreviewLoading(false);
          showErrorSnackbar("Something went wrong");
          if (screenId === userId) {
            closeDialog();
          }
        }
      },
      onSuccess() {
        if (changeState) {
          setPreviewLoading(false);
          if (screenId !== userId) {
            onChange(_opt, openInNew || false);
            showSuccessSnackbar("Successfully updated screen.");
            closeDialog();
          }
        }
      },
    });
  }

  function customizeDesign(screenId?: string) {
    if (opt.type === "layout") {
      onPreview({ option: opt, screenId, openInNew: true });
    } else if (opt.type === "template") {
      cloneDesign(screenId);
    }
  }

  async function cloneDesign(screenId?: string) {
    if (opt.type !== "template") return;

    openLoadingOverlay("Cloning design to your workspace...");

    _cloneDesign(opt, {
      onSuccess: (template) => {
        if (!template) return;
        onPreview({
          option: { ...template, type: "layout" },
          screenId,
          openInNew: true,
        })
      }
    })
  }

  function previewOnScreenButton({
    onClick,
    title,
    type,
  }: {
    onClick?: () => void;
    title?: string;
    type: "preview" | "set";
  }) {
    return (
      <Button
        variant={type === "preview" ? "default" : "filled"}
        size="sm"
        disabled={previewLoading}
        onClick={onClick}
      >
        {previewLoading ? (
          <CircularProgressIndicator />
        ) : (
          <PiTelevisionSimpleBold />
        )}
        {title ?? ""} {initialScreen?.name ?? "Screen"}
      </Button>
    );
  }

  function customizeDesignButton({ onClick }: { onClick?: () => void }) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onClick}
        disabled={previewLoading}
      >
        {previewLoading ? <CircularProgressIndicator /> : <MdFormatPaint />}
        Customize and use this design
      </Button>
    );
  }

  const screensWithSameAspectRatio = useMemo(
    () =>
      screens.filter(
        (e) =>
          e.id !== userId &&
          e.width &&
          e.height &&
          ((opt.type === "layout" || opt.type === "template") && opt?.width && opt?.height
            ? getAspectRatio(e.width, e.height) ===
            getAspectRatio(opt.width, opt.height)
            : true),
      ),
    [opt, screens, userId],
  );

  const screensWithDifferentAspectRatio = useMemo(
    () =>
      screens.filter(
        (e) =>
          screensWithSameAspectRatio.findIndex((item) => e.id === item.id) <
          0 && e.id !== userId,
      ),
    [screens, screensWithSameAspectRatio, userId],
  );

  const menuItems = [
    {
      title: "With Exact Aspect Ratio",
      items: screensWithSameAspectRatio,
    },
    {
      title: "Other Screens",
      items: screensWithDifferentAspectRatio,
    },
  ];

  const previewButtons: { title: string; variant: "preview" | "set" }[] = [
    { title: "Use this on", variant: "set" },
  ];

  useEffect(() => {
    // onPreview({ option: opt, screenId: userId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onClose() {
    if (pendingContentChange) {
      onPreview({
        option: pendingContentChange.opt,
        screenId: pendingContentChange.screenId,
        changeState: false,
      });
    }
    closeInitialDialog?.();
  }

  const isTemplateOrLayout = opt.type === "template" || opt.type === "layout"

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-8 p-4">
      <button
        type="button"
        className="absolute -right-2 -top-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        onClick={() => {
          onClose();
        }}
      >
        <Cross2Icon className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </button>
      <div className="w-full h-[70vh] outline outline-8 outline-black overflow-clip">
        <SignageContentPlayer hasInteracted renderContent loading={<>loading...</>}
          opt={opt}
          setMusicVolume={() => { }} />
      </div>
      {/* <iframe
        autoFocus={false}
        src={env.NEXT_PUBLIC_PLAYER_URL + "?hideLogs=true&from=dashboard"}
        className="pointer-events-none aspect-video max-h-[70vh] w-full outline outline-8 outline-black"
      /> */}

      <div className="flex flex-wrap items-center justify-center gap-4">
        {/* Customize Design Button */}
        {isTemplateOrLayout &&
          initialScreen &&
          customizeDesignButton({
            onClick() {
              customizeDesign();
            },
          })}

        {isTemplateOrLayout &&
          !initialScreen && (
            <CustomDropDownMenu
              menuButton={customizeDesignButton({})}
              menuItems={menuItems.map((menuItem) => ({
                title: menuItem.title,
                items: menuItem.items.map((e) => ({
                  title: e.name ?? "",
                  icon: screen?.id === e.id ? <MdDone /> : <FiMonitor />,
                  onClick() {
                    setScreen(e);
                    customizeDesign(e.id);
                  },
                })),
              }))}
            />
          )}

        {/* Preview on Screen Button */}
        {previewButtons.map((item) => (
          <>
            {initialScreen &&
              previewOnScreenButton({
                onClick() {
                  if (item.variant === "preview") {
                    setPendingContentChange({
                      opt,
                      screenId: initialScreen.id,
                    });
                  } else {
                    setPendingContentChange(undefined);
                  }
                  onPreview({});
                },
                title: item.title,
                type: item.variant,
              })}

            {!initialScreen && (
              <CustomDropDownMenu
                menuButton={previewOnScreenButton({
                  title: item.title,
                  type: item.variant,
                })}
                className="w-56"
                menuItems={menuItems.map((menuItem) => ({
                  title: menuItem.title,
                  items: menuItem.items.map((e) => ({
                    title: e.name ?? "",
                    icon: screen?.id === e.id ? <MdDone /> : <FiMonitor />,
                    onClick() {
                      setScreen(e);

                      if (pendingContentChange) {
                        onPreview({
                          option: pendingContentChange.opt,
                          screenId: pendingContentChange.screenId,
                          changeState: false,
                        });
                      }
                      if (item.variant === "preview") {
                        setPendingContentChange({
                          opt,
                          screenId: e.id,
                        });
                      } else {
                        setPendingContentChange(undefined);
                      }
                      onPreview({ screenId: e.id });
                    },
                  })),
                }))}
              />
            )}
          </>
        ))}
      </div>
    </div>
  );
}
